// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - TYPES DE NODES GRAFCET
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/grafcet/nodes/GrafcetNodeTypes.jsx
// Définition des blocs visuels pour l'éditeur GRAFCET
// Papy 73+ - Joshua approved! 🚀
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import '../styles/GrafcetNodes.css';

// ═══════════════════════════════════════════════════════════════════════════
// NODE DÉBUT (Cercle vert)
// ═══════════════════════════════════════════════════════════════════════════

const DebutNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--debut ${selected ? 'selected' : ''}`}>
      <div className="grafcet-node__icon">▶</div>
      <div className="grafcet-node__label">{data.label || 'Début'}</div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE FIN (Cercle rouge)
// ═══════════════════════════════════════════════════════════════════════════

const FinNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--fin ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__icon">■</div>
      <div className="grafcet-node__label">{data.label || 'Fin'}</div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE LECTURE CAPTEUR (Rectangle bleu avec icône capteur)
// ═══════════════════════════════════════════════════════════════════════════

const CapteurNode = memo(({ data, selected }) => {
  const capteurNom = data.config?.capteur_nom || 'Capteur';
  
  return (
    <div className={`grafcet-node grafcet-node--capteur ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">📊</span>
        <span className="grafcet-node__type">Capteur</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        {data.config?.capteur_id && (
          <div className="grafcet-node__detail">{capteurNom}</div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE CONDITION (Losange jaune)
// ═══════════════════════════════════════════════════════════════════════════

const ConditionNode = memo(({ data, selected }) => {
  const operateurs = {
    'lt': '<',
    'gt': '>',
    'eq': '=',
    'lte': '≤',
    'gte': '≥',
    'neq': '≠'
  };
  
  const op = operateurs[data.config?.operateur] || '?';
  const seuil = data.config?.seuil ?? '?';
  
  return (
    <div className={`grafcet-node grafcet-node--condition ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__diamond">
        <div className="grafcet-node__icon">❓</div>
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__condition">{op} {seuil}</div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="oui"
        className="grafcet-handle grafcet-handle--source grafcet-handle--oui"
        style={{ left: '30%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="non"
        className="grafcet-handle grafcet-handle--source grafcet-handle--non"
      />
      <div className="grafcet-node__labels">
        <span className="grafcet-label grafcet-label--oui">OUI</span>
        <span className="grafcet-label grafcet-label--non">NON</span>
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE ACTION (Rectangle violet)
// ═══════════════════════════════════════════════════════════════════════════

const ActionNode = memo(({ data, selected }) => {
  const commande = data.config?.commande || 'on';
  const commandeLabel = commande === 'on' ? '🟢 ON' : '🔴 OFF';
  
  return (
    <div className={`grafcet-node grafcet-node--action ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">⚡</span>
        <span className="grafcet-node__type">Action</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__detail">{commandeLabel}</div>
        {data.config?.duree && (
          <div className="grafcet-node__duration">⏱ {data.config.duree}s</div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE TIMER (Rectangle orange avec horloge)
// ═══════════════════════════════════════════════════════════════════════════

const TimerNode = memo(({ data, selected }) => {
  const duree = data.config?.duree || 60;
  const formatDuree = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s/60)}min`;
    return `${Math.floor(s/3600)}h${Math.floor((s%3600)/60)}min`;
  };
  
  return (
    <div className={`grafcet-node grafcet-node--timer ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">⏱️</span>
        <span className="grafcet-node__type">Timer</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__duration">{formatDuree(duree)}</div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE IA CONSEIL (Rectangle cyan avec robot)
// ═══════════════════════════════════════════════════════════════════════════

const IaNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--ia ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">🤖</span>
        <span className="grafcet-node__type">IA</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__detail">
          {data.config?.type_conseil || 'Optimisation'}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE NOTIFICATION (Rectangle rose avec cloche)
// ═══════════════════════════════════════════════════════════════════════════

const NotificationNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--notification ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">🔔</span>
        <span className="grafcet-node__type">Notif</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        {data.config?.message && (
          <div className="grafcet-node__detail grafcet-node__message">
            "{data.config.message.substring(0, 30)}..."
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE VALIDATION (Rectangle vert avec check)
// ═══════════════════════════════════════════════════════════════════════════

const ValidationNode = memo(({ data, selected }) => {
  const timeout = data.config?.timeout || 3600;
  const formatTimeout = (s) => {
    if (s < 3600) return `${Math.floor(s/60)}min`;
    return `${Math.floor(s/3600)}h`;
  };
  
  return (
    <div className={`grafcet-node grafcet-node--validation ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">✋</span>
        <span className="grafcet-node__type">Validation</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__detail">
          Timeout: {formatTimeout(timeout)}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="ok"
        className="grafcet-handle grafcet-handle--source"
        style={{ left: '30%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="timeout"
        className="grafcet-handle grafcet-handle--source grafcet-handle--timeout"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE PARALLELE DEBUT (Barre horizontale)
// ═══════════════════════════════════════════════════════════════════════════

const ParalleleDebutNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--parallele-debut ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__bar">
        <span className="grafcet-node__label">{data.label || 'Début //'}</span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="branch1"
        className="grafcet-handle grafcet-handle--source"
        style={{ left: '25%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="branch2"
        className="grafcet-handle grafcet-handle--source"
        style={{ left: '75%' }}
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE PARALLELE FIN (Barre horizontale)
// ═══════════════════════════════════════════════════════════════════════════

const ParalleleFinNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--parallele-fin ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        id="branch1"
        className="grafcet-handle grafcet-handle--target"
        style={{ left: '25%' }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="branch2"
        className="grafcet-handle grafcet-handle--target"
        style={{ left: '75%' }}
      />
      <div className="grafcet-node__bar">
        <span className="grafcet-node__label">{data.label || 'Fin //'}</span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE BOUCLE (Rectangle avec flèche circulaire)
// ═══════════════════════════════════════════════════════════════════════════

const BoucleNode = memo(({ data, selected }) => {
  const iterations = data.config?.iterations || 1;
  
  return (
    <div className={`grafcet-node grafcet-node--boucle ${selected ? 'selected' : ''} ${data.etat_actuel === 'en_cours' ? 'running' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__header">
        <span className="grafcet-node__icon">🔄</span>
        <span className="grafcet-node__type">Boucle</span>
      </div>
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label}</div>
        <div className="grafcet-node__detail">×{iterations}</div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
      <Handle
        type="source"
        position={Position.Left}
        id="loop"
        className="grafcet-handle grafcet-handle--source grafcet-handle--loop"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// NODE PAR DÉFAUT
// ═══════════════════════════════════════════════════════════════════════════

const DefaultNode = memo(({ data, selected }) => {
  return (
    <div className={`grafcet-node grafcet-node--default ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="grafcet-handle grafcet-handle--target"
      />
      <div className="grafcet-node__body">
        <div className="grafcet-node__label">{data.label || 'Étape'}</div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="grafcet-handle grafcet-handle--source"
      />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

const GrafcetNodeTypes = {
  debutNode: DebutNode,
  finNode: FinNode,
  capteurNode: CapteurNode,
  conditionNode: ConditionNode,
  actionNode: ActionNode,
  timerNode: TimerNode,
  iaNode: IaNode,
  notificationNode: NotificationNode,
  validationNode: ValidationNode,
  paralleleDebutNode: ParalleleDebutNode,
  paralleleFinNode: ParalleleFinNode,
  boucleNode: BoucleNode,
  defaultNode: DefaultNode
};

export default GrafcetNodeTypes;
